import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopeParams } from "./useScopeParams";
import { insightsService } from "@/services/insightsService";
import { alertsService } from "@/services/alertsService";
import { InsightProps } from "@/components/Insights/insights";

type ActionType =
  | "creating"
  | "editing"
  | "deleting"
  | "deleting alert"
  | "archiving"
  | "activating"
  | "sharing"
  | "unsharing";

export const useInsightMutations = ({
  openSuccessNotification,
  openErrorNotification,
}: {
  openSuccessNotification: (action: ActionType) => void;
  openErrorNotification: (action: ActionType) => void;
}) => {
  const { id } = useScopeParams();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return await insightsService.createInsight(id, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["insights", id] });

      openSuccessNotification("creating");
    },
    onError: (error) => {
      console.error("Error creating insight:", error);
      openErrorNotification("creating");
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({
      insightId,
      payload,
    }: {
      insightId: string;
      payload: Record<string, unknown>;
      action?: ActionType;
    }) => {
      return await insightsService.editInsight(id, insightId, payload);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["insights", id] });

      const action = variables.action || "editing";
      openSuccessNotification(action);
    },
    onError: (error, variables) => {
      console.error("Error editing insight:", error);
      const action = variables.action || "editing";
      openErrorNotification(action);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      insightId,
      type = "insight",
    }: {
      insightId: string;
      type?: "insight" | "alert";
    }) => {
      if (type === "alert") {
        await alertsService.deleteAlert(id, insightId);
        return { id: insightId, type: "alert" as const };
      }
      await insightsService.deleteInsight(id, insightId);
      return { id: insightId, type: "insight" as const };
    },
    onSuccess: async (data) => {
      if (data.type === "alert") {
        queryClient.setQueryData(
          ["insights", id],

          (oldData: InsightProps[] = []) => {
            if (!oldData) return oldData;
            return oldData.map((insight) => {
              if (insight.alert?.id === data.id) {
                return { ...insight, alert: null };
              }
              return insight;
            });
          },
        );
      }
      // For real mode, invalidate to refetch both insights and alerts
      await queryClient.invalidateQueries({ queryKey: ["insights", id] });
      await queryClient.invalidateQueries({ queryKey: ["alerts", id] });

      const action = data.type === "alert" ? "deleting alert" : "deleting";
      openSuccessNotification(action as ActionType);
    },
    onError: (error, variables) => {
      const errorType =
        variables.type === "alert" ? "deleting alert" : "deleting";
      console.error(`Error ${errorType}:`, error);
      openErrorNotification(errorType as ActionType);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (insight: InsightProps) => {
      await insightsService.archiveInsight(id, insight.id);
      if (insight.alert?.id) {
        await alertsService.updateAlert(id, insight.alert.id, {
          is_archived: true,
        });
      }
      return {
        ...insight,
        updated_at: new Date().toISOString(),
      };
    },
    onSuccess: async () => {
      // For real mode, invalidate to refetch
      await queryClient.invalidateQueries({ queryKey: ["insights", id] });
      await queryClient.invalidateQueries({ queryKey: ["alerts", id] });

      openSuccessNotification("archiving");
    },
    onError: (error) => {
      console.error("Error archiving insight:", error);
      openErrorNotification("archiving");
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (insight: InsightProps) => {
      await insightsService.activateInsight(id, insight.id);
      if (insight.alert?.id) {
        await alertsService.updateAlert(id, insight.alert.id, {
          is_archived: false,
        });
      }
      return {
        ...insight,
        is_archived: false,
        updated_at: new Date().toISOString(),
      };
    },
    onSuccess: async () => {
      // For real mode, invalidate to refetch
      await queryClient.invalidateQueries({ queryKey: ["insights", id] });
      await queryClient.invalidateQueries({ queryKey: ["alerts", id] });

      openSuccessNotification("activating");
    },
    onError: (error) => {
      console.error("Error activating insight:", error);
      openErrorNotification("activating");
    },
  });

  return {
    handleCreateInsight: createMutation.mutate,
    handleEditInsight: editMutation.mutate,
    handleDelete: deleteMutation.mutate,
    handleArchiveInsight: archiveMutation.mutate,
    handleActivateInsight: activateMutation.mutate,
    confirmSubmitLoading: createMutation.isPending || editMutation.isPending,
    isCreateError: createMutation.isError,
    // The panel shows the failure's status alongside its generic copy —
    // "Something went wrong" alone left a create failure undiagnosable
    // without opening the network tab.
    createError: createMutation.error,
    resetCreateInsight: createMutation.reset,
    confirmDeleteLoading: deleteMutation.isPending,
    confirmArchiveLoading: archiveMutation.isPending,
    confirmActiveLoading: activateMutation.isPending,
  };
};
